/**
 * BottomRadioPlayer
 * Persistent bottom player that stays visible across page navigation
 */
import React from 'react';
import { Play, Pause, X, Volume2, VolumeX, Radio, Loader2 } from 'lucide-react';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';

const BottomRadioPlayer = () => {
  const {
    currentStation,
    isPlaying,
    isLoading,
    volume,
    nowPlaying,
    togglePlayPause,
    stopPlayback,
    changeVolume
  } = useRadioPlayer();

  // Don't render if no station is selected
  if (!currentStation) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] border-t border-white/10 shadow-lg z-50"
      data-testid="bottom-radio-player"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          
          {/* Left: Station Info */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {/* Station Icon/Logo */}
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-[#FF9F0A] to-[#FF6B00] flex items-center justify-center">
              <Radio className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            
            {/* Station Name & Now Playing */}
            <div className="min-w-0 flex-1">
              <h4 className="text-white font-semibold text-sm sm:text-base truncate">
                {currentStation.name}
              </h4>
              {nowPlaying && (nowPlaying.artist || nowPlaying.title) ? (
                <p className="text-white/60 text-xs sm:text-sm truncate">
                  {nowPlaying.artist} {nowPlaying.title ? `- ${nowPlaying.title}` : ''}
                </p>
              ) : (
                <p className="text-white/40 text-xs sm:text-sm">
                  {isLoading ? 'Σύνδεση...' : 'Live Radio'}
                </p>
              )}
            </div>
          </div>

          {/* Center: Play Controls */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FF9F0A] hover:bg-[#FF8C00] flex items-center justify-center transition-all hover:scale-105 disabled:opacity-50"
              data-testid="player-play-pause"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              ) : (
                <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" />
              )}
            </button>
          </div>

          {/* Right: Volume & Close */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Volume Control - Hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => changeVolume(volume === 0 ? 0.7 : 0)}
                className="text-white/60 hover:text-white transition-colors"
              >
                {volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-3
                  [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-[#FF9F0A]
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
            </div>

            {/* Close Button */}
            <button
              onClick={stopPlayback}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="player-close"
            >
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottomRadioPlayer;
